// src/services/requestQueue.js
// Request queue with concurrency limiting and retry logic

class RequestQueue {
  constructor(options = {}) {
    this.maxConcurrent = options.maxConcurrent || 4;
    this.retryAttempts = options.retryAttempts || 3;
    this.retryDelay = options.retryDelay || 1000; // Base delay in ms
    // 2026-09-22: only 502 (a dead upstream hop) and network errors are retried
    // here. 503/504 are the backend saying it is saturated; retrying them from
    // every tab is what turns "slow" into "down". 429 is the admission gate and
    // is owned by utils/retryOnBusy — retrying it here as well turned one
    // gated call into up to 24 requests.
    this.retryStatusCodes = options.retryStatusCodes || [502];
    // 2026-10-14: a request enqueued with retryable: false is never replayed.
    // The client sets it for every non-GET: a POST whose response was lost
    // after the backend ran it (a 5xx without CORS headers surfaces as
    // "Failed to fetch") was re-sent with the same body ~1s later, creating
    // duplicate encounter + clinical_note pairs on one appointment.

    this.activeRequests = 0;
    this.queue = [];
    this.paused = false;

    // Debug mode - set to true to see queue activity
    this.debug = options.debug || false;
  }

  log(...args) {
    if (this.debug) {
    }
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Async function that performs the fetch
   * @param {Object} options - Request options
   * @param {number} options.priority - Higher priority requests are processed first (default: 0)
   * @param {string} options.label - Optional label for debugging
   * @param {boolean} options.retryable - false disables retry (mutations)
   * @returns {Promise} - Resolves with the request result
   */
  enqueue(requestFn, options = {}) {
    const { priority = 0, label = 'request', signal = null, retryable = true } = options;

    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Request aborted', 'AbortError'));
        return;
      }

      const queueItem = {
        requestFn,
        resolve,
        reject,
        priority,
        label,
        signal,
        retryable,
        attempt: 1,
        addedAt: Date.now()
      };

      // Insert based on priority (higher priority first)
      const insertIndex = this.queue.findIndex(item => item.priority < priority);
      if (insertIndex === -1) {
        this.queue.push(queueItem);
      } else {
        this.queue.splice(insertIndex, 0, queueItem);
      }

      this.log(`Enqueued: ${label} (priority: ${priority}, queue length: ${this.queue.length})`);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.paused) {
      this.log('Queue is paused');
      return;
    }

    if (this.activeRequests >= this.maxConcurrent) {
      this.log(`At max concurrency (${this.activeRequests}/${this.maxConcurrent})`);
      return;
    }

    if (this.queue.length === 0) {
      this.log('Queue is empty');
      return;
    }

    // Drop requests whose signal aborted while queued, without taking a
    // concurrency slot — otherwise a burst of cancelled calls still serializes
    // real work behind maxConcurrent.
    let item = this.queue.shift();
    while (item && item.signal?.aborted) {
      item.reject(new DOMException('Request aborted', 'AbortError'));
      this.log(`Dropped aborted: ${item.label} (queued: ${this.queue.length})`);
      item = this.queue.shift();
    }
    if (!item) {
      return;
    }
    this.activeRequests++;

    this.log(`Processing: ${item.label} (active: ${this.activeRequests}, queued: ${this.queue.length})`);

    let result;
    let error;
    let failed = false;
    try {
      result = await item.requestFn();
    } catch (e) {
      error = e;
      failed = true;
    } finally {
      // The slot is released BEFORE any retry wait. A sleeping retry used to
      // hold one of the 4 slots, so a 5xx storm froze the whole tab's queue
      // and users reloaded — a fresh tab being 4 more slots of load.
      this.activeRequests--;
    }

    if (!failed) {
      item.resolve(result);
    } else {
      const attempt = item.attempt || 1;
      if (!item.signal?.aborted && item.retryable !== false && this.shouldRetry(error, attempt)) {
        const delay = this.calculateDelay(attempt);
        this.log(`Retry ${attempt}/${this.retryAttempts} for ${item.label} after ${delay}ms`);
        setTimeout(() => {
          if (item.signal?.aborted) {
            item.reject(new DOMException('Request aborted', 'AbortError'));
            return;
          }
          item.attempt = attempt + 1;
          // Retries keep their turn ahead of newer work of the same priority,
          // but never pre-empt higher-priority work enqueued meanwhile
          const at = this.queue.findIndex(q => q.priority <= item.priority);
          if (at === -1) this.queue.push(item); else this.queue.splice(at, 0, item);
          this.processQueue();
        }, delay);
      } else {
        item.reject(error);
      }
    }

    this.log(`Completed: ${item.label} (active: ${this.activeRequests}, queued: ${this.queue.length})`);

    // Process next item in queue
    this.processQueue();
  }

  shouldRetry(error, attempt) {
    // Caller cancels and our own timeouts are never retried
    if (error?.name === 'AbortError' || error?.name === 'TimeoutError') {
      return false;
    }

    if (attempt >= this.retryAttempts) {
      return false;
    }

    // Retry on network errors
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      return true;
    }

    // Retry on specific status codes
    if (error.status && this.retryStatusCodes.includes(error.status)) {
      return true;
    }

    return false;
  }

  calculateDelay(attempt) {
    // Exponential backoff with jitter
    const exponentialDelay = this.retryDelay * Math.pow(2, attempt - 1);
    const jitter = Math.random() * 500; // 0-500ms jitter
    return Math.min(exponentialDelay + jitter, 10000); // Cap at 10 seconds
  }

  /**
   * Pause processing (doesn't affect active requests)
   */
  pause() {
    this.paused = true;
    this.log('Queue paused');
  }

  /**
   * Resume processing
   */
  resume() {
    this.paused = false;
    this.log('Queue resumed');
    this.processQueue();
  }

  /**
   * Clear all pending requests
   * @param {Error} error - Optional error to reject pending requests with
   */
  clear(error = new Error('Queue cleared')) {
    const pending = this.queue.length;
    this.queue.forEach(item => item.reject(error));
    this.queue = [];
    this.log(`Cleared ${pending} pending requests`);
  }

  /**
   * Get queue statistics
   */
  getStats() {
    return {
      active: this.activeRequests,
      queued: this.queue.length,
      maxConcurrent: this.maxConcurrent,
      paused: this.paused
    };
  }
}

// Create singleton instance with default settings.
// retryAttempts 2 = one retry (2026-09-22; was 3 attempts on 502/503/504/429).
const requestQueue = new RequestQueue({
  maxConcurrent: 4,
  retryAttempts: 2,
  retryDelay: 1000,
  debug: process.env.NODE_ENV === 'development'
});

// Priority levels for common request types
export const PRIORITY = {
  CRITICAL: 100,    // Auth, user profile
  HIGH: 75,         // Feature flags, permissions
  NORMAL: 50,       // Page data
  LOW: 25,          // Analytics, background sync
  BACKGROUND: 0     // Prefetching, non-essential
};

export { RequestQueue, requestQueue };
export default requestQueue;
